import {
  CreateMarketingAdsetRequest,
  AdPlatform,
  CampaignType,
  DisplayMarketingAdSet,
  DisplayMarketingCampaign,
  MarketingCustomAudience,
  AudienceData,
  AdSetStatus,
} from "common/interfaces/KYDMarketing";
import { AdSetFormData } from "../forms/adset";
import { mapCustomAudiencesToServerFormat } from "./customAudienceMapper";
import dayjs from "dayjs";
import { MetaAdsPixel } from "@common/interfaces/MetaTypes";

// Helper function to format datetime to YYYY-MM-DDTHH:MM format
const formatDateTimeForInput = (
  dateTime: string | null | undefined
): string | null => {
  if (!dateTime) return null;

  const date = dayjs(dateTime);
  if (!date.isValid()) return null;

  // Format to YYYY-MM-DDTHH:MM (datetime-local input format)
  return date.format("YYYY-MM-DDTHH:mm");
};

// Helper function to transform MarketingCustomAudience to form format
const transformCustomAudience = (audience: MarketingCustomAudience) => {
  const base = {
    id: audience.id || undefined,
    external_id: audience.external_id,
    name: audience.name,
    audience_type: audience.type,
    created_at: audience.metadata?.updated_time,
  };

  switch (audience.type) {
    case "csv":
      return {
        ...base,
        description: audience.metadata?.description || "",
        file_key: audience.metadata?.file_key || "",
        mapping: audience.metadata?.mapping || [],
        schema_fields: audience.metadata?.schema_fields || [],
        audience_type: "csv" as const,
      };
    case "kyd":
      return {
        ...base,
        description: audience.metadata?.description,
        genres:
          audience.metadata?.genres?.map((g) => ({ key: g, value: g })) || [],
        artists:
          audience.metadata?.artists?.map((a) => ({ key: a, value: a })) || [],
        lookalike: audience.metadata?.lookalike_ratio
          ? {
              name: audience.metadata.lookalike_name, // LookalikeRatio is just a string, not an object
              ratio: audience.metadata.lookalike_ratio,
            }
          : undefined,
        audience_type: "kyd" as const,
      };
    case "page_lookalike":
      return {
        ...base,
        description: audience.metadata?.description || "",
        page_type: audience.metadata?.page_type || "facebook",
        page_id: audience.metadata?.page_id || "",
        page_name: audience.metadata?.page_name || "",
        lookalike: audience.metadata?.lookalike_ratio
          ? {
              name: audience.metadata.lookalike_name, // LookalikeRatio is just a string, not an object
              ratio: audience.metadata.lookalike_ratio,
            }
          : undefined,
        audience_type: "page_lookalike" as const,
      };
    case "page_engagers":
      return {
        ...base,
        description: audience.metadata?.description || "",
        page_type: audience.metadata?.page_type || "facebook",
        page_id: audience.metadata?.page_id || "",
        page_name: audience.metadata?.page_name || "",
        audience_type: "page_engagers" as const,
      };
    case "meta":
      // For meta audiences, we need to handle the fact that the metadata structure
      // doesn't include all the meta-specific fields in the base interface
      return {
        ...base,
        description: audience.metadata?.description || "",
        subtype: "", // Not available in base metadata
        time_created: audience.metadata?.created_time,
        time_updated: audience.metadata?.updated_time,
        data_source: undefined, // Not available in base metadata
        delivery_status: undefined, // Not available in base metadata
        approximate_count_lower_bound: undefined, // Not available in base metadata
        approximate_count_upper_bound: undefined, // Not available in base metadata
        audience_type: "meta" as const,
      };
    default:
      // Fallback to CSV format for unknown types
      return {
        ...base,
        description: audience.metadata?.description || "",
        file_key: audience.metadata?.file_key || "",
        mapping: audience.metadata?.mapping || [],
        schema_fields: audience.metadata?.schema_fields || [],
        audience_type: "csv" as const,
      };
  }
};

export interface AdsetFormValues extends AdSetFormData {
  // Additional form-specific properties can be added here if needed
}

const transform = {
  input: ({
    adset,
    campaign,
    pixels,
  }: {
    adset?: DisplayMarketingAdSet;
    campaign?: DisplayMarketingCampaign;
    pixels?: MetaAdsPixel[];
  }): AdsetFormValues => {
    const custom_audiences_with_repeats = (
      adset?.targeting?.custom_audiences || []
    ).concat(campaign?.targeting?.custom_audiences || []);

    const custom_audiences = custom_audiences_with_repeats.filter(
      (ca, index, self) => index === self.findIndex((t) => t.id === ca.id)
    );

    // Transform custom audiences from server format to form format
    const transformed_custom_audiences = custom_audiences.map(
      transformCustomAudience
    );

    return {
      id: adset?.id || "",
      external_id: adset?.external_id || "",
      name: adset?.name || "",
      parent_id: adset?.parent_id || campaign?.id || "",
      campaign_type: campaign?.campaign_type || "",
      account_id: adset?.account_id || campaign?.account_id || "",
      platform: adset?.platform || AdPlatform.META,
      timezone: adset?.timezone || "UTC",
      budget_type: adset?.budget?.amount?.amount ? "adset" : "campaign",

      budget: adset?.budget
        ? {
            type: adset?.budget?.type,
            amount: {
              amount: adset?.budget?.amount?.units || 0,
              currency: adset?.budget?.amount?.currency || "USD",
            },
          }
        : {
            type: "daily",
            amount: {
              amount: 0,
              currency: "USD",
            },
          },
      // display_budget: adset?.display_budget,

      // Schedule - extract from schedule object and format for datetime-local input
      start_time: formatDateTimeForInput(adset?.schedule?.start_time),
      stop_time: formatDateTimeForInput(adset?.schedule?.stop_time),

      // Targeting - extract from nested targeting object
      age_min: adset?.targeting?.demographics?.age_min || 18,
      age_max: adset?.targeting?.demographics?.age_max || 65,
      genders: adset?.targeting?.demographics?.genders.at(0),

      countries: adset?.targeting?.locations?.countries || [
        { key: "US", value: "US" },
      ],
      geo_markets: adset?.targeting?.locations?.geo_markets || [],
      regions: adset?.targeting?.locations?.regions || [],
      cities: adset?.targeting?.locations?.cities || [],
      interests: adset?.targeting?.interests || [],
      device_platforms: adset?.targeting?.device_platforms || [],

      // // Placements - extract from placements object
      // facebook_positions: adset?.placements?.facebook?.positions || [],
      // instagram_positions: adset?.placements?.instagram?.positions || [],

      // Custom audiences
      custom_audiences: transformed_custom_audiences,

      audience_type:
        transformed_custom_audiences.length > 0 ||
        adset?.targeting?.interests?.length > 0
          ? "manual"
          : adset?.audience_type || "advantage",

      // Tracking
      pixel_id: adset?.tracking?.pixel_id || pixels?.at(0)?.id || null,
      custom_conversion_event: adset?.tracking?.custom_conversion_event || true,

      // Legacy fields
      optimization_goal: null,
      targeting_query: "",
      targeting_type: null,
      targeting_subtype: null,
      audience_ids: [],
    };
  },

  output: ({
    data,
  }: {
    data: AdsetFormValues;
  }): CreateMarketingAdsetRequest => {
    return {
      id: data.id || undefined,
      external_id: data.external_id || undefined,
      name: data.name,
      parent_id: data.parent_id,
      account_id: data.account_id,
      // status: data.status || AdSetStatus.ACTIVE,

      budget_type: data.budget_type as "campaign" | "adset",

      // Transform budget to Price object
      budget:
        data.budget?.type && data.budget?.amount
          ? {
              type: data.budget?.type as "daily" | "lifetime",
              amount: {
                amount: data.budget?.amount?.amount || 0,
                currency: data.budget?.amount?.currency || "USD",
              },
            }
          : undefined,

      // Transform schedule
      schedule:
        data.start_time || data.stop_time
          ? {
              start_time: data.start_time || undefined,
              stop_time: data.stop_time || undefined,
              timezone: data.timezone,
            }
          : undefined,

      campaign_type: data.campaign_type as CampaignType,

      // Transform targeting
      targeting:
        data.age_min ||
        data.age_max ||
        data.genders ||
        data.countries.length > 0 ||
        data.geo_markets.length > 0 ||
        data.regions.length > 0 ||
        data.cities.length > 0 ||
        data.interests.length > 0 ||
        data.device_platforms.length > 0 ||
        data.custom_audiences.length > 0
          ? {
              locations: {
                countries:
                  data.countries.length > 0
                    ? data.countries
                        .filter((c) => c.key && c.value)
                        .map((c) => ({ key: c.key!, value: c.value! }))
                    : undefined,
                geo_markets:
                  data.geo_markets.length > 0
                    ? data.geo_markets
                        .filter((g) => g.key && g.value && g.country)
                        .map((g) => ({
                          key: g.key!,
                          country: g.country!,
                          name: g.value!,
                          value: g.value!,
                        }))
                    : undefined,
                regions:
                  data.regions.length > 0
                    ? data.regions
                        .filter((r) => r.key && r.value)
                        .map((r) => ({ key: r.key!, value: r.value! }))
                    : undefined,
                cities:
                  data.cities.length > 0
                    ? data.cities
                        .filter((c) => c.key && c.value)
                        .map((c) => ({ key: c.key!, value: c.value! }))
                    : undefined,
              },
              demographics:
                data.age_min || data.age_max || data.genders
                  ? {
                      age_min: data.age_min || undefined,
                      age_max: data.age_max || undefined,
                      genders: data.genders,
                    }
                  : undefined,
              interests:
                data.interests.length > 0
                  ? data.interests
                      .filter((i) => i.key && i.value)
                      .map((i) => ({ key: i.key!, value: i.value! }))
                  : undefined,
              device_platforms:
                data.device_platforms.length > 0
                  ? data.device_platforms
                      .filter((d) => d.key && d.value)
                      .map((d) => ({ key: d.key!, value: d.value! }))
                  : undefined,
              custom_audiences:
                data.custom_audiences.length > 0
                  ? mapCustomAudiencesToServerFormat(data.custom_audiences)
                  : undefined,
            }
          : undefined,

      // // Transform placements
      // placements: {
      //   facebook: {
      //     positions: data.facebook_positions as any, // Type assertion for now
      //   },
      //   instagram: {
      //     positions: data.instagram_positions as any, // Type assertion for now
      //   },
      // },

      audience_type: data.audience_type as "advantage" | "manual",

      // Transform tracking
      tracking: {
        pixel_id: data.pixel_id || undefined,
        custom_conversion_event: data.custom_conversion_event,
      },

      platform: data.platform as AdPlatform,
      timezone: data.timezone,
    };
  },
};

export default transform;
